/**
 * Service for Places API
 * Manages places data (enterprises, clubs, favorites, etc.)
 */


const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/places';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que le lieu visé appartient bien à l'appelant.
 * Il lui faut donc une identité prouvée : le JWT signé au login, et non le
 * `createdById`/`peopleId` que le corps de la requête déclarait jusqu'ici —
 * ce dernier venait du client, et se remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Place types
 */
export const PLACE_TYPES = {
    ENTERPRISE: 'enterprise',
    CLUB: 'club',
    FAVORITE: 'favorite',
    OTHER: 'other'
};

/**
 * Place type labels (French)
 */
export const PLACE_TYPE_LABELS = {
    enterprise: 'Entreprise',
    club: 'Club',
    favorite: 'Lieu favori',
    other: 'Autre'
};

/**
 * Map of lowercase keys to camelCase
 * API returns lowercase (placename, tvanum) but frontend expects camelCase (placeName, tvaNum)
 */
const keyMapping = {
    placename: 'placeName',
    placetypecode: 'placeTypeCode',
    googleplaceid: 'googlePlaceId',
    postalcode: 'postalCode',
    countrycode: 'countryCode',
    tvanum: 'tvaNum',
    createdbyid: 'createdById',
    createdat: 'createdAt',
    updatedat: 'updatedAt',
    placeuuid: 'placeUuid',
    contactname: 'contactName',
    firmtype: 'firmType',
    banknum: 'bankNum',
    facebookurl: 'facebookUrl',
    twitterurl: 'twitterUrl',
    youtubeurl: 'youtubeUrl',
    googleurl: 'googleUrl',
    modifiedbyid: 'modifiedById',
    imageurl: 'imageUrl',
    linkedplaceid: 'linkedPlaceId',
    linkedplaceuuid: 'linkedPlaceUuid',
    isactive: 'isActive',
    ispublic: 'isPublic',
    isvalidated: 'isValidated',
    isowner: 'isOwner',
    isfavorite: 'isFavorite',
    ismember: 'isMember',
    createdbypeopleid: 'createdByPeopleId',
    createdbyname: 'createdByName',
    websiteurl: 'websiteUrl',
    contactperson: 'contactPerson',
    legalform: 'legalForm',
    relationshiptype: 'relationshipType',
    localuuid: 'localUuid',
    publicplaceid: 'publicPlaceId'
};

/**
 * Normalize object keys to camelCase
 * Handles both lowercase (from API) and UPPERCASE keys
 */
const normalizeKeys = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                const lowerKey = key.toLowerCase();
                // Use mapping if exists, otherwise keep original key
                const newKey = keyMapping[lowerKey] || lowerKey;
                return [newKey, normalizeKeys(value)];
            })
        );
    }
    return obj;
};

/**
 * Get all places with pagination and filtering
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Places array
 */
export const getAllPlaces = async (options = {}) => {
    try {
        const { page = 1, limit = 20, search = '', placeTypeCode = '' } = options;

        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });

        if (search) {
            params.append('search', search);
        }

        if (placeTypeCode) {
            params.append('placeTypeCode', placeTypeCode);
        }

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching places:', error);
        throw error;
    }
};

/**
 * Get a place by ID
 * @param {number} id - Place ID
 * @returns {Promise<Object>} Place object
 */
export const getPlaceById = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA);
    } catch (error) {
        console.error('Error fetching place:', error);
        throw error;
    }
};

/**
 * Create a new place
 * @param {Object} placeData - Place data
 * @returns {Promise<Object>} Created place
 */
export const createPlace = async (placeData) => {
    try {

        // Ensure placeTypeCode is set
        const dataToSend = {
            ...placeData,
            placeTypeCode: placeData.placeTypeCode || PLACE_TYPES.ENTERPRISE
        };

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: enTetesEcriture(),
            credentials: 'include',
            body: JSON.stringify(dataToSend)
        });

        const result = await response.json();

        if (!response.ok) {
            // Check for duplicate error
            if (result.message?.includes('duplicate') || result.message?.includes('unique')) {
                throw new Error('Ce lieu existe déjà (numéro de TVA ou adresse identique)');
            }
            throw new Error(result.message || 'Error creating place');
        }

        return {
            success: true,
            data: normalizeKeys(result.data || result.DATA)
        };
    } catch (error) {
        console.error('Error creating place:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Update a place
 * @param {number} id - Place ID
 * @param {Object} placeData - Updated data
 * @returns {Promise<Object>} Updated place
 */
export const updatePlace = async (id, placeData) => {
    try {

        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            credentials: 'include',
            body: JSON.stringify(placeData)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Error updating place');
        }

        return {
            success: true,
            data: normalizeKeys(result.data || result.DATA)
        };
    } catch (error) {
        console.error('Error updating place:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Delete a place
 * @param {number} id - Place ID
 * @returns {Promise<Object>} Result
 */
export const deletePlace = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false),
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error deleting place:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Get places for a specific user by type
 * @param {number} peopleId - User ID
 * @param {string} placeTypeCode - Place type code filter (optional)
 * @returns {Promise<Array>} User's places
 */
export const getPlacesByPeopleId = async (peopleId, placeTypeCode = '') => {
    try {
        const params = new URLSearchParams();
        if (placeTypeCode) {
            params.append('placeTypeCode', placeTypeCode);
        }

        const url = `${API_BASE_URL}/people/${peopleId}${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        const places = normalizeKeys(result.data || result.DATA || []);
        // Filtre côté client en attendant que le cache CFC se recharge
        if (placeTypeCode) {
            return places.filter(p => (p.placeTypeCode || p.placetypecode || '').toLowerCase() === placeTypeCode.toLowerCase());
        }
        return places;
    } catch (error) {
        console.error('Error fetching user places:', error);
        return [];
    }
};

/**
 * Get user's enterprises
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} User's enterprises
 */
export const getUserEnterprises = async (peopleId) => {
    return getPlacesByPeopleId(peopleId, PLACE_TYPES.ENTERPRISE);
};

/**
 * Get user's clubs
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} User's clubs
 */
export const getUserClubs = async (peopleId) => {
    return getPlacesByPeopleId(peopleId, PLACE_TYPES.CLUB);
};

/**
 * Get user's favorite places
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} User's favorite places
 */
export const getUserFavorites = async (peopleId) => {
    return getPlacesByPeopleId(peopleId, PLACE_TYPES.FAVORITE);
};

/**
 * Link a place to a user (people_places junction)
 * @param {number} peopleId - User ID
 * @param {string} placeUuid - Place UUID
 * @param {string} role - Role/relationship (owner, employee, worker, client, visitor)
 * @param {boolean} isFavorite - Add to favorites
 * @param {string} localUuid - Optional local UUID for client tracking
 * @returns {Promise<Object>} Result
 */
export const linkPlaceToPeople = async (peopleId, placeUuid, role = 'visitor', isFavorite = false, localUuid = '', isMember = false) => {
    try {
        const response = await fetch(`${API_BASE_URL}/link`, {
            method: 'POST',
            headers: enTetesEcriture(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, placeUuid, localUuid, role, isFavorite, isMember })
        });

        const result = await response.json();

        return {
            success: response.ok,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message
        };
    } catch (error) {
        console.error('Error linking place to people:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Unlink a place from a user
 * @param {number} peopleId - User ID
 * @param {string} placeUuid - Place UUID
 * @returns {Promise<Object>} Result
 */
export const unlinkPlaceFromPeople = async (peopleId, placeUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/unlink`, {
            method: 'POST',
            headers: enTetesEcriture(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, placeUuid })
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error unlinking place from people:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Get user's relation with a specific place
 * @param {string} placeUuid - Place UUID
 * @param {number} peopleId - People ID
 * @returns {Promise<Object>} Relation data (role, isFavorite)
 */
export const getPlaceRelation = async (placeUuid, peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/relation/${placeUuid}?peopleId=${peopleId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        // Handle both lowercase and uppercase keys from ColdFusion
        const data = result.data || result.DATA;
        const success = result.success !== undefined ? result.success : result.SUCCESS;

        if (success && data && Object.keys(data).length > 0) {
            const normalized = normalizeKeys(data);
            return {
                success: true,
                data: normalized
            };
        }

        return {
            success: true,
            data: null // No relation exists
        };
    } catch (error) {
        console.error('Error fetching place relation:', error);
        return {
            success: false,
            data: null,
            message: error.message
        };
    }
};

/**
 * Search places by keyword
 * @param {string} keyword - Search term
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Matching places
 */
export const searchPlaces = async (keyword, limit = 10) => {
    try {
        if (!keyword || keyword.length < 2) {
            return [];
        }

        const params = new URLSearchParams({
            search: keyword,
            limit: limit.toString()
        });

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error searching places:', error);
        return [];
    }
};

/**
 * Find or create a place from Google Places data
 * Creates the place if it doesn't exist (checks by googlePlaceId)
 * @param {Object} googlePlaceData - Data from Google Places API
 * @param {number} createdById - User ID creating the place
 * @returns {Promise<Object>} Place object (existing or newly created)
 */
export const findOrCreatePlaceFromGoogle = async (googlePlaceData, createdById) => {
    try {
        // First check if place exists by name/city
        const existingPlaces = await searchPlaces(googlePlaceData.placeName, 5);

        // Try to find exact match by googlePlaceId
        const exactMatch = existingPlaces.find(p =>
            p.googlePlaceId === googlePlaceData.googlePlaceId ||
            (p.placeName === googlePlaceData.placeName && p.city === googlePlaceData.city)
        );

        if (exactMatch) {
            return { success: true, data: exactMatch, isNew: false };
        }

        // Create new place with split address fields
        const placeData = {
            placeName: googlePlaceData.placeName,
            googlePlaceId: googlePlaceData.googlePlaceId || '',
            street: googlePlaceData.street || '',
            number: googlePlaceData.number || '',
            box: googlePlaceData.box || '',
            postalCode: googlePlaceData.postalCode || '',
            city: googlePlaceData.city || '',
            country: googlePlaceData.country || '',
            countryCode: googlePlaceData.countryCode || '',
            latitude: googlePlaceData.latitude,
            longitude: googlePlaceData.longitude,
            placeTypeCode: PLACE_TYPES.FAVORITE,
            createdById
        };

        const result = await createPlace(placeData);
        if (result.success) {
            return { success: true, data: result.data, isNew: true };
        }

        return { success: false, message: result.message };
    } catch (error) {
        console.error('Error finding/creating place:', error);
        return { success: false, message: error.message };
    }
};

export default {
    getAllPlaces,
    getPlaceById,
    createPlace,
    updatePlace,
    deletePlace,
    getPlacesByPeopleId,
    getUserEnterprises,
    getUserClubs,
    getUserFavorites,
    linkPlaceToPeople,
    unlinkPlaceFromPeople,
    getPlaceRelation,
    searchPlaces,
    findOrCreatePlaceFromGoogle,
    PLACE_TYPES,
    PLACE_TYPE_LABELS
};
